import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Icon, { LoadingOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import { setAppliedSort } from '@store/metricLens/base/actions';
import { shouldCalcPercentage } from '@utils/metrics';
import {
  appliedSortSelector,
  allFiltersSelector,
  unitModeSelector,
  liveModeSelector,
} from '@store/metricLens/base/selectors';
import {
  loadingSelector,
  historicalRangeTotalDataSelector,
} from '@store/metricLens/historicalRangeData/selectors';
import { liveLoadSelector, liveRangeTotalDataSelector } from '@store/metricLens/liveData/selectors';
import arrow from '@src/icons/two-sided-arrow.svg';
import { track } from '@utils';
import { CLICK_ML_SORTING } from '@src/utils/sensor/track';
import { humanizeValue } from './utils';
import {
  StyledColumnHeader,
  StyledColumnHeaderText,
  StyledSortTag,
  StyledSortLoader,
  StyledSortContainer,
} from './styled';
import { tsAtomMap } from './hooks/useTimeseries';
// eslint-disable-next-line react/display-name
export default (props) => {
  const { metric } = props;
  const liveMode = useSelector(liveModeSelector);
  const historicalTotalData = liveMode
    ? useSelector(liveRangeTotalDataSelector)
    : useSelector(historicalRangeTotalDataSelector);
  return (
    <StyledColumnHeader>
      <StyledColumnHeaderText title={metric.displayName}>
        {metric.short || ''}
        <span>{metric.subscript || ''}</span>
      </StyledColumnHeaderText>
      <StyledSortContainer>
        {historicalTotalData.map((total, index) => (
          <EachSortCell
            key={`${metric}${index}`}
            currentMetric={metric.name}
            index={index}
            data={total[metric.name]}
            unit={metric.unit}
          />
        ))}
      </StyledSortContainer>
    </StyledColumnHeader>
  );
};

const EachSortCell = (props) => {
  const { data, unit, currentMetric, index } = props;
  const antIcon = <LoadingOutlined spin />;
  const liveMode = useSelector(liveModeSelector);
  const liveLoad = useSelector(liveLoadSelector);
  const load = useSelector(loadingSelector);
  const loadData = liveMode ? liveLoad : load;
  const [active, setActive] = useState(false);
  const sort = useSelector(appliedSortSelector);
  const allFilters = useSelector(allFiltersSelector);
  const unitMode = useSelector(unitModeSelector);
  const [totalData, setTotalData] = useState({});
  const dispatch = useDispatch();
  let color = '';
  if (allFilters[index]) {
    color = allFilters[index].color;
  }
  const handleSortChange = () => {
    const { filter, metric } = sort;
    let sortOrder = 'desc';
    if (filter === index && metric === currentMetric) {
      sortOrder = sort.order === 'desc' ? 'asc' : 'desc';
    }
    dispatch(
      setAppliedSort({
        filter: index,
        metric: currentMetric,
        order: sortOrder,
      }),
    );

    // clear all ts row states
    tsAtomMap.clear();

    track({
      event: CLICK_ML_SORTING,
      data: {
        metric: currentMetric,
        order: sortOrder,
      },
    });
  };

  useEffect(() => {
    const { filter, metric } = sort;
    if (filter === index && metric === currentMetric) {
      setActive(true);
    } else {
      setActive(false);
    }
  }, [sort, index, currentMetric]);

  useEffect(() => {
    let parsedSortData = {
      humanizedNumber: '',
      humanizedUnit: '',
    };
    if (unitMode === '%' && shouldCalcPercentage({ metricKey: currentMetric })) {
      parsedSortData = {
        humanizedNumber: '100',
        humanizedUnit: '%',
      };
    } else {
      const humanizedData = humanizeValue(data, unit);
      parsedSortData = humanizedData;
    }
    setTotalData(parsedSortData);
  }, [currentMetric, data, unit, unitMode, index]);

  return (
    <StyledSortTag
      color={color}
      active={active}
      className={`${sort.order} ${active ? 'active' : null}`}
      icon={arrow}
      onClick={handleSortChange}
    >
      {loadData.loading ? (
        <StyledSortLoader>
          <Spin indicator={antIcon} />
        </StyledSortLoader>
      ) : (
        <>
          {totalData.humanizedNumber || 'NA'} {totalData.humanizedUnit}
          {active ? null : <Icon component={arrow} />}
        </>
      )}
    </StyledSortTag>
  );
};
